import { useId, useRef, useState } from "react";
import type { Picture } from "@repo/shared-types";
import { PictureFrame } from "@/components/picture-frame";
import { PictureButton } from "@/components/form";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";

/**
 * An event's image (7g): a 16:9 banner across the top of its feed card and
 * its page, set in the posting and editing forms by the owner and admins
 * holding manage_events. Kept as long as the event is, and replaced or taken
 * down from its edit form.
 */

/**
 * The banner, while the event has one ready. Decorative beside the event's
 * title, which every place showing it prints. Rounded on its own; a place
 * that sets it flush in a card's edge passes its own corners.
 */
export function EventBanner({ image, alt = "", className }: { image: Picture | null; alt?: string; className?: string }) {
  // Which picture has arrived, so a replaced one fades in afresh. One that
  // fails shows as it is, with its alt, rather than staying hidden.
  const [arrived, setArrived] = useState<string | null>(null);
  if (!image?.url) return null;
  const url = image.url;
  // The muted box holds its place; the picture fades in over it once it has
  // arrived, rather than appearing whenever it happens to finish (0081).
  return (
    <div className={cn("aspect-video w-full overflow-hidden rounded-3xl bg-muted", className)}>
      <img
        src={url}
        alt={alt}
        width={image.width ?? undefined}
        height={image.height ?? undefined}
        loading="lazy"
        onLoad={() => setArrived(url)}
        onError={() => setArrived(url)}
        className={cn(
          "size-full object-cover transition-opacity duration-(--motion-fade) motion-reduce:transition-none",
          arrived !== url && "opacity-0",
        )}
      />
    </div>
  );
}

/**
 * The image box of an event's form: a file to choose, framed 16:9 by dragging
 * it, and — editing one that has an image — the image there now, with a box to
 * take it down. Nothing is sent until the form is: the event's action posts
 * the event, then the picture, which belongs to an event that exists.
 *
 * The form holding it is sent as multipart/form-data, or the file is left
 * behind. The box to take it down is shadcn's checkbox, which sends
 * `removeImage=on` with the form it sits in, as a native one would.
 */
export function EventImageField({ current }: { current: Picture | null }) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  return (
    <div className="space-y-3">
      <span className="block text-sm font-medium">Image</span>
      {file ? (
        <PictureFrame file={file} aspect="16:9" name="imageCrop" />
      ) : (
        current?.url && (
          <div className="space-y-3">
            <EventBanner image={current} alt="The image now" />
            <Field orientation="horizontal">
              <Checkbox id={`${id}-remove`} name="removeImage" />
              <FieldLabel htmlFor={`${id}-remove`} className="font-normal">
                Remove the image
              </FieldLabel>
            </Field>
          </div>
        )
      )}
      <div className="flex flex-wrap items-center gap-2">
        <PictureButton name="image" inputRef={input} onChange={(event) => setFile(event.currentTarget.files?.[0] ?? null)}>
          {file ? "Choose another" : current?.url ? "Replace the image" : "Choose an image"}
        </PictureButton>
        {file && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (input.current) input.current.value = "";
              setFile(null);
            }}
          >
            Don't use it
          </Button>
        )}
        {file && <span className="text-sm text-muted-foreground">Drag it to frame it; zoom with the slider.</span>}
      </div>
    </div>
  );
}
